import React from 'react';

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed' | 'draft' | 'open';
  date: Date;
  description: string;
  downloadUrl?: string;
  invoiceNumber?: string;
}

export interface BillingHistoryProps {
  invoices: Invoice[];
  loading?: boolean;
  onInvoiceDownload?: (invoiceId: string) => void;
  className?: string;
}

export const BillingHistory: React.FC<BillingHistoryProps> = ({
  invoices,
  loading = false,
  onInvoiceDownload,
  className = '',
}) => {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'failed':
        return '#ef4444';
      case 'draft':
        return '#6b7280';
      case 'open':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      case 'draft':
        return 'Draft';
      case 'open':
        return 'Open';
      default:
        return status;
    }
  };

  const handleDownload = (invoice: Invoice) => {
    if (onInvoiceDownload) {
      onInvoiceDownload(invoice.id);
    } else if (invoice.downloadUrl) {
      window.open(invoice.downloadUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className={className} style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem',
            color: '#6b7280',
          }}
        >
          <div
            style={{
              width: '2rem',
              height: '2rem',
              border: '2px solid #e5e7eb',
              borderTop: '2px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '1rem',
            }}
          ></div>
          <p>Loading billing history...</p>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className={className} style={{ width: '100%' }}>
        <div
          style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6b7280',
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#374151',
              margin: '0 0 0.5rem 0',
            }}
          >
            No billing history
          </h3>
          <p style={{ margin: 0 }}>
            Your invoices will appear here once you start making payments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ width: '100%' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 0.5rem 0',
          }}
        >
          Billing History
        </h2>
        <p style={{ color: '#6b7280', margin: 0 }}>
          View and download your past invoices
        </p>
      </div>

      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden',
          background: 'white',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr 120px 100px 120px',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div
            style={{
              padding: '1rem',
              fontWeight: 600,
              color: '#374151',
              fontSize: '0.875rem',
            }}
          >
            Date
          </div>
          <div
            style={{
              padding: '1rem',
              fontWeight: 600,
              color: '#374151',
              fontSize: '0.875rem',
            }}
          >
            Description
          </div>
          <div
            style={{
              padding: '1rem',
              fontWeight: 600,
              color: '#374151',
              fontSize: '0.875rem',
            }}
          >
            Amount
          </div>
          <div
            style={{
              padding: '1rem',
              fontWeight: 600,
              color: '#374151',
              fontSize: '0.875rem',
            }}
          >
            Status
          </div>
          <div
            style={{
              padding: '1rem',
              fontWeight: 600,
              color: '#374151',
              fontSize: '0.875rem',
            }}
          >
            Actions
          </div>
        </div>

        <div>
          {invoices.map(invoice => (
            <div
              key={invoice.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr 120px 100px 120px',
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  color: '#6b7280',
                }}
              >
                {formatDate(invoice.date)}
              </div>
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  fontSize: '0.875rem',
                }}
              >
                <span style={{ color: '#1f2937', fontWeight: 500 }}>
                  {invoice.description}
                </span>
                {invoice.invoiceNumber && (
                  <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                    #{invoice.invoiceNumber}
                  </span>
                )}
              </div>
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#1f2937',
                }}
              >
                {formatAmount(invoice.amount, invoice.currency)}
              </div>
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                }}
              >
                <span
                  style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    backgroundColor: getStatusColor(invoice.status),
                  }}
                >
                  {getStatusText(invoice.status)}
                </span>
              </div>
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                }}
              >
                {(invoice.downloadUrl || onInvoiceDownload) &&
                  invoice.status === 'paid' && (
                    <button
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#374151',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleDownload(invoice)}
                      title="Download invoice"
                    >
                      <span>⬇️</span>
                      Download
                    </button>
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
